/**
 * useHeicImage.js
 *
 * A React hook that converts a HEIC image URL to a renderable JPEG Blob URL,
 * with full caching, in-flight deduplication, and memory safety.
 *
 * Usage:
 *   const { src, isConverting } = useHeicImage(url, mimeType);
 *
 * - `src`          → the URL to pass to <img src={...} />.
 *                    Starts as the original URL; becomes the Blob URL once converted.
 * - `isConverting` → boolean; true while the conversion is in progress.
 *
 * The hook is safe to use in lists – multiple instances pointing to the same
 * HEIC URL share a single conversion (via the heicCache in-flight deduplication)
 * and the same Blob URL (from the module-level LRU cache).
 *
 * Memory management:
 *   - On unmount, `releaseUrl` is called. The Blob URL is only revoked by the
 *     cache's LRU eviction logic once no component is using it.
 *   - On URL change, the old URL is released before converting the new one.
 */

import { useState, useEffect, useRef } from 'react';
import { getConvertedUrl, releaseUrl, isHeicUrl } from '../utils/heicCache';

/**
 * @param {string}  url      – Original image URL (may be HEIC or any other type).
 * @param {string}  mimeType – MIME type string, e.g. 'image/heic'. Optional.
 * @returns {{ src: string, isConverting: boolean }}
 */
const useHeicImage = (url, mimeType = '') => {
    // Start with the original URL so the <img> has something to render immediately
    // (for non-HEIC images this is the final value and never changes).
    const [src, setSrc] = useState(url);
    const [isConverting, setIsConverting] = useState(false);

    // Track the URL we are currently holding a cache ref for, so we can release
    // it correctly when the component's url prop changes or the component unmounts.
    const heldUrlRef = useRef(null);

    useEffect(() => {
        // Nothing to do for empty URLs
        if (!url) {
            setSrc('');
            setIsConverting(false);
            return;
        }

        // Non-HEIC: just update src and bail out, no cache interaction needed.
        if (!isHeicUrl(url, mimeType)) {
            // Release any previously held HEIC cache ref
            if (heldUrlRef.current) {
                releaseUrl(heldUrlRef.current);
                heldUrlRef.current = null;
            }
            setSrc(url);
            setIsConverting(false);
            return;
        }

        // HEIC path ────────────────────────────────────────────────────────────
        let cancelled = false;

        const convert = async () => {
            setIsConverting(true);

            // Release the previously held URL ref (url changed)
            if (heldUrlRef.current && heldUrlRef.current !== url) {
                releaseUrl(heldUrlRef.current);
                heldUrlRef.current = null;
            }

            try {
                // getConvertedUrl handles caching + deduplication internally.
                // The ref-count is incremented inside getConvertedUrl.
                const convertedUrl = await getConvertedUrl(url, mimeType);

                if (!cancelled) {
                    heldUrlRef.current = url; // remember we now hold a ref for this url
                    setSrc(convertedUrl);
                    setIsConverting(false);
                } else {
                    // Component unmounted or url changed while converting:
                    // we must release the ref we just acquired.
                    releaseUrl(url);
                }
            } catch {
                if (!cancelled) {
                    setSrc(url); // fallback to original
                    setIsConverting(false);
                }
            }
        };

        convert();

        return () => {
            cancelled = true;
        };
    }, [url, mimeType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Release the cache ref on final unmount
    useEffect(() => {
        return () => {
            if (heldUrlRef.current) {
                releaseUrl(heldUrlRef.current);
                heldUrlRef.current = null;
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return { src, isConverting };
};

export default useHeicImage;
